import { PDFDocument, PDFFont, PDFPage, StandardFonts, rgb } from 'pdf-lib';

const CURRENT_VERSION_NOTICE_TITLE = 'Current Version';
const CURRENT_VERSION_NOTICE_BODY =
  'This is the most current version of this clinical review letter. ' +
  'Previous versions have been superseded and are archived for reference only.';

const NOTE_LABEL = 'Note:';
const NOTE_BODY =
  'This is the most current version of this letter. ' +
  'Previous versions are now superseded and archived for reference only.';

const EDITED_BY_PREFIX = 'Edited by ';

const hexColor = (hex: string) => {
  const value = hex.replace('#', '');
  return rgb(
    parseInt(value.slice(0, 2), 16) / 255,
    parseInt(value.slice(2, 4), 16) / 255,
    parseInt(value.slice(4, 6), 16) / 255
  );
};

const BANNER_BACKGROUND_COLOR = hexColor('#FFF8ED');
const BANNER_BORDER_COLOR = hexColor('#FF6900');
const BANNER_TITLE_COLOR = hexColor('#9F2D00');
const BANNER_BODY_COLOR = hexColor('#CD4000');

const NOTE_LABEL_COLOR = hexColor('#CC0000');
const NOTE_BODY_COLOR = hexColor('#1A1A1A');
const EDITED_BY_COLOR = hexColor('#999999');

const PAGE_MARGIN = 40;

const BORDER_WIDTH = 4;
const BANNER_PADDING_X = 16;
const BANNER_PADDING_Y = 10;
const BANNER_GAP = 4;
const TITLE_FONT_SIZE = 13;
const TITLE_LINE_HEIGHT = 16;
const BANNER_BODY_FONT_SIZE = 10;
const BANNER_BODY_LINE_HEIGHT = 13;
const TOP_GAP = BANNER_BODY_LINE_HEIGHT * 2;

const FOOTER_GAP_ABOVE_SEPARATOR = 18;
const FOOTER_GAP_BELOW_SEPARATOR = 10;
const NOTE_FONT_SIZE = 10;
const NOTE_LINE_HEIGHT = 14;
const EDITED_BY_FONT_SIZE = 9;
const EDITED_BY_LINE_HEIGHT = 12;
const PARAGRAPH_GAP = 6;
const FOOTER_BOTTOM_PADDING = PAGE_MARGIN;

const wrapText = (text: string, font: PDFFont, fontSize: number, maxWidth: number): string[] => {
  const lines: string[] = [];
  let currentLine = '';

  text.split(' ').forEach((word) => {
    const candidateLine = currentLine ? `${currentLine} ${word}` : word;
    if (currentLine && font.widthOfTextAtSize(candidateLine, fontSize) > maxWidth) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = candidateLine;
    }
  });

  if (currentLine) lines.push(currentLine);
  return lines;
};

type WordToken = {
  word: string;
  font: PDFFont;
  size: number;
  color: ReturnType<typeof rgb>;
};

type TextRun = Omit<WordToken, 'word'> & {
  text: string;
};

const tokenizeRuns = (runs: TextRun[]): WordToken[] =>
  runs.flatMap(({ text, ...style }) =>
    text
      .split(' ')
      .filter(Boolean)
      .map((word) => ({ word, ...style }))
  );

const layoutTextLines = (tokens: WordToken[], maxWidth: number): WordToken[][] => {
  const lines: WordToken[][] = [];
  let currentLine: WordToken[] = [];
  let currentWidth = 0;

  tokens.forEach((token) => {
    const wordWidth = token.font.widthOfTextAtSize(token.word, token.size);
    const spaceWidth = currentLine.length ? token.font.widthOfTextAtSize(' ', token.size) : 0;

    if (currentLine.length && currentWidth + spaceWidth + wordWidth > maxWidth) {
      lines.push(currentLine);
      currentLine = [token];
      currentWidth = wordWidth;
    } else {
      currentLine.push(token);
      currentWidth += spaceWidth + wordWidth;
    }
  });

  if (currentLine.length) lines.push(currentLine);
  return lines;
};

const drawTextLines = (
  page: PDFPage,
  lines: WordToken[][],
  x: number,
  topY: number,
  lineHeight: number,
  fontSize: number
) => {
  lines.forEach((line, lineIndex) => {
    let cursorX = x;
    const baselineY = topY - lineIndex * lineHeight - fontSize;

    line.forEach((token, tokenIndex) => {
      page.drawText(token.word, {
        x: cursorX,
        y: baselineY,
        size: token.size,
        font: token.font,
        color: token.color
      });

      const wordWidth = token.font.widthOfTextAtSize(token.word, token.size);
      const spaceWidth = tokenIndex < line.length - 1 ? token.font.widthOfTextAtSize(' ', token.size) : 0;
      cursorX += wordWidth + spaceWidth;
    });
  });
};

export const annotateCurrentLetterPdf = async (
  pdfBytes: ArrayBuffer,
  editedByName: string,
  editedByDate: string
): Promise<Uint8Array> => {
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const firstPage = pdfDoc.getPage(0);
  const lastPageIndex = pdfDoc.getPageCount() - 1;
  const lastPage = pdfDoc.getPage(lastPageIndex);

  const { width: firstWidth, height: firstHeight } = firstPage.getSize();
  const { width: lastWidth } = lastPage.getSize();

  const bannerX = PAGE_MARGIN;
  const bannerWidth = firstWidth - PAGE_MARGIN * 2;
  const bannerTextX = bannerX + BORDER_WIDTH + BANNER_PADDING_X;
  const bannerTextMaxWidth = bannerWidth - BORDER_WIDTH - BANNER_PADDING_X * 2;
  const bannerBodyLines = wrapText(CURRENT_VERSION_NOTICE_BODY, regularFont, BANNER_BODY_FONT_SIZE, bannerTextMaxWidth);
  const bannerHeight = BANNER_PADDING_Y * 2 + TITLE_LINE_HEIGHT + BANNER_GAP + bannerBodyLines.length * BANNER_BODY_LINE_HEIGHT;
  const noticeHeight = TOP_GAP + bannerHeight;

  const footerTextMaxWidth = lastWidth - PAGE_MARGIN * 2;
  const noteLines = layoutTextLines(
    tokenizeRuns([
      { text: NOTE_LABEL, font: boldFont, size: NOTE_FONT_SIZE, color: NOTE_LABEL_COLOR },
      { text: NOTE_BODY, font: regularFont, size: NOTE_FONT_SIZE, color: NOTE_BODY_COLOR }
    ]),
    footerTextMaxWidth
  );
  const editedByLines = layoutTextLines(
    tokenizeRuns([
      { text: EDITED_BY_PREFIX, font: regularFont, size: EDITED_BY_FONT_SIZE, color: EDITED_BY_COLOR },
      { text: editedByName, font: boldFont, size: EDITED_BY_FONT_SIZE, color: EDITED_BY_COLOR },
      { text: `on ${editedByDate}`, font: regularFont, size: EDITED_BY_FONT_SIZE, color: EDITED_BY_COLOR }
    ]),
    footerTextMaxWidth
  );
  const footerHeight =
    FOOTER_GAP_ABOVE_SEPARATOR +
    FOOTER_GAP_BELOW_SEPARATOR +
    noteLines.length * NOTE_LINE_HEIGHT +
    PARAGRAPH_GAP +
    editedByLines.length * EDITED_BY_LINE_HEIGHT +
    FOOTER_BOTTOM_PADDING;

  const drawBanner = (page: PDFPage, contentTopY: number) => {
    page.drawRectangle({
      x: bannerX,
      y: contentTopY,
      width: bannerWidth,
      height: bannerHeight,
      color: BANNER_BACKGROUND_COLOR
    });

    page.drawRectangle({
      x: bannerX,
      y: contentTopY,
      width: BORDER_WIDTH,
      height: bannerHeight,
      color: BANNER_BORDER_COLOR
    });

    let cursorY = contentTopY + bannerHeight - BANNER_PADDING_Y;

    cursorY -= TITLE_FONT_SIZE;
    page.drawText(CURRENT_VERSION_NOTICE_TITLE, {
      x: bannerTextX,
      y: cursorY,
      size: TITLE_FONT_SIZE,
      font: boldFont,
      color: BANNER_TITLE_COLOR
    });
    cursorY -= TITLE_LINE_HEIGHT - TITLE_FONT_SIZE + BANNER_GAP;

    bannerBodyLines.forEach((line) => {
      cursorY -= BANNER_BODY_FONT_SIZE;
      page.drawText(line, {
        x: bannerTextX,
        y: cursorY,
        size: BANNER_BODY_FONT_SIZE,
        font: regularFont,
        color: BANNER_BODY_COLOR
      });
      cursorY -= BANNER_BODY_LINE_HEIGHT - BANNER_BODY_FONT_SIZE;
    });
  };

  const drawFooter = (page: PDFPage, pageWidth: number) => {
    const separatorY = footerHeight - FOOTER_GAP_ABOVE_SEPARATOR;
    page.drawLine({
      start: { x: PAGE_MARGIN, y: separatorY },
      end: { x: pageWidth - PAGE_MARGIN, y: separatorY },
      thickness: 1,
      color: BANNER_BORDER_COLOR
    });

    let topY = separatorY - FOOTER_GAP_BELOW_SEPARATOR;
    drawTextLines(page, noteLines, PAGE_MARGIN, topY, NOTE_LINE_HEIGHT, NOTE_FONT_SIZE);

    topY -= noteLines.length * NOTE_LINE_HEIGHT + PARAGRAPH_GAP;
    drawTextLines(page, editedByLines, PAGE_MARGIN, topY, EDITED_BY_LINE_HEIGHT, EDITED_BY_FONT_SIZE);
  };

  if (lastPageIndex === 0) {
    const embedded = await pdfDoc.embedPage(firstPage);
    pdfDoc.removePage(0);

    const newPage = pdfDoc.insertPage(0, [firstWidth, firstHeight + noticeHeight]);
    newPage.drawPage(embedded, { x: 0, y: 0, width: firstWidth, height: firstHeight });

    drawBanner(newPage, firstHeight);
    drawFooter(newPage, firstWidth);
  } else {
    const embeddedFirst = await pdfDoc.embedPage(firstPage);
    pdfDoc.removePage(0);

    const newFirstPage = pdfDoc.insertPage(0, [firstWidth, firstHeight + noticeHeight]);
    newFirstPage.drawPage(embeddedFirst, { x: 0, y: 0, width: firstWidth, height: firstHeight });
    drawBanner(newFirstPage, firstHeight);

    drawFooter(lastPage, lastWidth);
  }

  return pdfDoc.save();
};
